import React, { useCallback, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  ClipCategory, ClipLabelVocabulary, NewClipLabel, VideoClip,
} from 'services/VideoEditingService';
import { Draft, EMPTY_CLIP_DRAFT, clipLabelEditFrom } from 'pages/VideoEditing/components/ClipLabelFields';
import ClipLabelSummary from 'pages/VideoEditing/components/ClipLabelSummary';
import ClipLabelsDialog from 'pages/VideoEditing/components/ClipLabelsDialog';
import ClipTrimmer, { ClipTrimmerRange } from 'pages/VideoEditing/components/ClipTrimmer';

interface AddClipDialogProps {
  // A raw file in a Drive folder, not a library entry — this dialog is what turns one into
  // the other.
  clip: VideoClip;
  vocabulary: ClipLabelVocabulary | null;
  categories: ClipCategory[] | null;
  onCreateCategory: (name: string) => Promise<ClipCategory>;
  onClose: () => void;
  onSave: (input: NewClipLabel) => Promise<void>;
}

/**
 * Add one Drive clip to the b-roll library, trimmed and filed.
 *
 * The same composition as ClipEditDialog — trimmer on top, a static label summary with its
 * own "Edit details" modal below — minus everything that assumes an existing library row.
 * There's no "save as new clip" here, because there is no source entry to carve from: this
 * IS the create.
 *
 * The range starts as the whole clip, so "add this take as it is" is one button and trimming
 * is opt-in. Confidence is never offered: a person filing a clip by hand is the authority on
 * what it shows, and the scale only ever described how sure the model was.
 */
export default function AddClipDialog({
  clip, vocabulary, categories, onCreateCategory, onClose, onSave,
}: AddClipDialogProps) {
  // Safe to seed straight from the shared constant: every edit here replaces the draft
  // ({ ...draft, ...patch }) rather than mutating it, so the constant is never written through.
  const [draft, setDraft] = useState<Draft>(EMPTY_CLIP_DRAFT);
  const [range, setRange] = useState<ClipTrimmerRange & { changed: boolean; valid: boolean }>({
    inSec: 0,
    outSec: clip.durationSec ?? 0,
    changed: false,
    // Stays false until the trimmer has loaded a real duration off the video. Drive reports
    // durationSec as null for a file it hasn't finished processing, and a create with
    // outSec 0 is rejected server-side.
    valid: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingLabels, setEditingLabels] = useState(false);

  const handleRangeChange = useCallback(
    (next: ClipTrimmerRange & { changed: boolean; valid: boolean }) => setRange(next),
    [],
  );

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await onSave({
        ...clipLabelEditFrom(draft, { categoriesOffered: !!categories }),
        fileId: clip.fileId,
        inSec: range.inSec,
        outSec: range.outSec,
        creationSource: 'library_import',
      });
      onClose();
    } catch (err: any) {
      // Inline rather than a page-level snackbar: the person is looking at this form, and the
      // backend's messages here name the fix ("open its folder in the Video Editing tool first").
      setError(err?.response?.data?.error || err?.message || 'Could not add this clip');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Add to the B-Roll Library
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {clip.name}
            {' · the whole clip is selected — drag the handles to add just part of it'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <ClipTrimmer
            fileId={clip.fileId}
            streamUrl={clip.streamUrl}
            // One clip per mounted dialog and the initial range never moves, so the file is
            // the whole boundary — unlike the edit dialog, which reopens over a clip whose
            // stored range may have just changed.
            resetKey={clip.fileId}
            knownDuration={clip.durationSec}
            width={clip.width}
            height={clip.height}
            initialInSec={0}
            initialOutSec={clip.durationSec}
            onRangeChange={handleRangeChange}
          />
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <ClipLabelSummary
                draft={draft}
                vocabulary={vocabulary}
                categories={categories}
                humanLabeled
              />
            </Box>
            <Button
              size="small"
              startIcon={<EditOutlinedIcon fontSize="small" />}
              onClick={() => setEditingLabels(true)}
              disabled={saving}
            >
              Edit details
            </Button>
          </Box>
          {/* A nudge, not a gate — the library searches without a product just fine. */}
          {!draft.productCategory && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              No product set. It’ll still be searchable, but filing it under one makes it much
              easier to find later.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !range.valid}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {saving ? 'Adding…' : 'Add to library'}
          </Button>
        </DialogActions>
      </Dialog>

      {editingLabels && (
        <ClipLabelsDialog
          draft={draft}
          vocabulary={vocabulary}
          categories={categories}
          onCreateCategory={onCreateCategory}
          showConfidence={false}
          onDone={(next) => { setDraft(next); setEditingLabels(false); }}
          onCancel={() => setEditingLabels(false)}
        />
      )}
    </>
  );
}
